export interface ModelSpecs {
  brand: string
  model: string
  displayName: string
  requiredOpening: { widthInches: number; heightInchesMin: number; heightInchesMax: number; depthInches: number }
  electrical: 'Hardwired (direct wire)' | '120V plug' | 'Either'
  unitWeightLbs: number
  doorClearanceInches: number
  depthWithDoorInches: number
  needsExtraSupport: boolean
  /**
   * True when these specs came from the local catalog. False when synthesized
   * from a parsed brand+model that didn't match any catalog entry, or when
   * the unconditional FALLBACK_MODEL was returned. UI should soften any
   * "loaded from manufacturer database" copy when this is false.
   */
  fromCatalog: boolean
}

export const FALLBACK_MODEL: ModelSpecs = {
  brand: 'Generic',
  model: 'Generic 24" Built-In',
  displayName: 'Generic 24" Built-In Dishwasher',
  requiredOpening: { widthInches: 24, heightInchesMin: 33.5, heightInchesMax: 35, depthInches: 24 },
  electrical: 'Either',
  unitWeightLbs: 60,
  doorClearanceInches: 21.5,
  depthWithDoorInches: 24.5,
  needsExtraSupport: false,
  fromCatalog: false,
}

const TABLE: Record<string, ModelSpecs> = {
  WDT730PAHZ: {
    brand: 'Whirlpool', model: 'WDT730PAHZ', displayName: 'Whirlpool WDT730PAHZ 24" Built-In Dishwasher',
    requiredOpening: { widthInches: 24, heightInchesMin: 33.5, heightInchesMax: 35, depthInches: 24 },
    electrical: 'Hardwired (direct wire)', unitWeightLbs: 56.2,
    doorClearanceInches: 21.5, depthWithDoorInches: 24.5, needsExtraSupport: false,
    fromCatalog: true,
  },
  SHPM88Z75N: {
    brand: 'Bosch', model: 'SHPM88Z75N', displayName: 'Bosch SHPM88Z75N 800 Series 24"',
    requiredOpening: { widthInches: 24, heightInchesMin: 33.875, heightInchesMax: 34.875, depthInches: 23.625 },
    electrical: '120V plug', unitWeightLbs: 75.4,
    doorClearanceInches: 22, depthWithDoorInches: 25.5, needsExtraSupport: false,
    fromCatalog: true,
  },
  GDT665SSNSS: {
    brand: 'GE', model: 'GDT665SSNSS', displayName: 'GE GDT665SSNSS 24" Built-In',
    requiredOpening: { widthInches: 24, heightInchesMin: 33.5, heightInchesMax: 35, depthInches: 24 },
    electrical: 'Either', unitWeightLbs: 61.0,
    doorClearanceInches: 21.5, depthWithDoorInches: 24.625, needsExtraSupport: false,
    fromCatalog: true,
  },
  PDT715SYNFS: {
    brand: 'GE Profile', model: 'PDT715SYNFS', displayName: 'GE Profile PDT715SYNFS 24"',
    requiredOpening: { widthInches: 24, heightInchesMin: 33.5, heightInchesMax: 35, depthInches: 24 },
    electrical: 'Either', unitWeightLbs: 70.0,
    doorClearanceInches: 22, depthWithDoorInches: 24.5, needsExtraSupport: false,
    fromCatalog: true,
  },
  KDPM604KPS: {
    brand: 'KitchenAid', model: 'KDPM604KPS', displayName: 'KitchenAid KDPM604KPS 24"',
    requiredOpening: { widthInches: 24, heightInchesMin: 33.5, heightInchesMax: 35, depthInches: 24 },
    electrical: 'Hardwired (direct wire)', unitWeightLbs: 67.5,
    doorClearanceInches: 21.75, depthWithDoorInches: 24.875, needsExtraSupport: false,
    fromCatalog: true,
  },
  DW80R9950US: {
    brand: 'Samsung', model: 'DW80R9950US', displayName: 'Samsung DW80R9950US Linear Wash 24"',
    requiredOpening: { widthInches: 24, heightInchesMin: 33.875, heightInchesMax: 34.875, depthInches: 24 },
    electrical: '120V plug', unitWeightLbs: 72.0,
    doorClearanceInches: 22, depthWithDoorInches: 24.5, needsExtraSupport: false,
    fromCatalog: true,
  },
  LDT7808SS: {
    brand: 'LG', model: 'LDT7808SS', displayName: 'LG LDT7808SS 24" TrueSteam',
    requiredOpening: { widthInches: 24, heightInchesMin: 33.5, heightInchesMax: 35, depthInches: 24 },
    electrical: 'Hardwired (direct wire)', unitWeightLbs: 69.5,
    doorClearanceInches: 21.5, depthWithDoorInches: 24.625, needsExtraSupport: false,
    fromCatalog: true,
  },
  MDB7959SKZ: {
    brand: 'Maytag', model: 'MDB7959SKZ', displayName: 'Maytag MDB7959SKZ 24"',
    requiredOpening: { widthInches: 24, heightInchesMin: 33.5, heightInchesMax: 35, depthInches: 24 },
    electrical: 'Either', unitWeightLbs: 60.0,
    doorClearanceInches: 21.5, depthWithDoorInches: 24.5, needsExtraSupport: false,
    fromCatalog: true,
  },
  FFID2426TS: {
    brand: 'Frigidaire', model: 'FFID2426TS', displayName: 'Frigidaire FFID2426TS 24"',
    requiredOpening: { widthInches: 24, heightInchesMin: 33.5, heightInchesMax: 35, depthInches: 24 },
    electrical: '120V plug', unitWeightLbs: 55.0,
    doorClearanceInches: 21.5, depthWithDoorInches: 24.5, needsExtraSupport: false,
    fromCatalog: true,
  },
}

/**
 * Look up a parsed dishwasher by brand + model. If the model number matches
 * the local catalog, returns the full spec sheet. Otherwise synthesizes a
 * displayName from the parsed brand/model so the UI shows the customer's
 * real dishwasher instead of a generic placeholder — falling back to
 * FALLBACK_MODEL only when both inputs are empty.
 */
export const lookupModel = (brand: string, modelNumber: string): ModelSpecs => {
  const key = modelNumber.trim().toUpperCase()
  const match = TABLE[key]
  if (match) return match
  const cleanBrand = brand.trim()
  const cleanModel = modelNumber.trim()
  if (cleanBrand || cleanModel) {
    return {
      ...FALLBACK_MODEL,
      brand: cleanBrand || FALLBACK_MODEL.brand,
      model: cleanModel || FALLBACK_MODEL.model,
      displayName: cleanBrand && cleanModel
        ? `${cleanBrand} ${cleanModel}`
        : (cleanModel || cleanBrand),
    }
  }
  return FALLBACK_MODEL
}
